import { NavLink } from 'react-router-dom';
import { SECTIONS } from '../lib/navigation';
import { cn } from './ui';

/**
 * Menu de seções, em faixa própria abaixo do cabeçalho.
 *
 * Ficava na mesma linha do nome da aplicação, e não dava para distinguir um do
 * outro: "Portal do Fornecedor" e "Fornecedores" eram texto escuro do mesmo
 * tamanho a 16px de distância — um rótulo e um link com a mesma aparência.
 *
 * A separação agora é por papel, e não por espaçamento. Linha de cima: quem
 * somos e quem está logado, tudo estático. Linha de baixo: só navegação, com
 * ícone e aba sublinhada. Três sinais redundantes (posição, ícone, sublinhado)
 * porque um só falha para quem lê a tela rápido — que foi exatamente o caso.
 *
 * A lista vem de `SECTIONS`, em `lib/navigation.ts`: este componente não conhece
 * nenhuma rota do Portal do Fornecedor e serve a qualquer app do weloze.lab.
 */
export function SectionNav({ isWeloze, isAdmin }: { isWeloze: boolean; isAdmin: boolean }) {
  const sections = SECTIONS.filter((section) => {
    if (section.adminOnly && !isAdmin) return false;
    return isWeloze ? !section.supplierOnly : !section.welozeOnly;
  });

  // Com uma seção só não há para onde navegar: a faixa seria enfeite.
  if (sections.length < 2) return null;

  return (
    // O `-mb-px` fica aqui, e não na aba: ele sobe a borda do cabeçalho 1px
    // para o sublinhado da aba ativa cobri-la. Na aba, esse pixel sobrava para
    // fora do container rolável — e como `overflow-x: auto` obriga o eixo Y a
    // virar `auto` também, 1px de sobra desenhava uma barra de rolagem vertical
    // inteira na faixa do menu.
    <nav aria-label="Seções" className="-mb-px border-t border-slate-100">
      {/* `overflow-x-auto` para o menu crescer no celular sem estourar a
          largura — na fatia (c) entram RNCs e certificações. */}
      <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-2 sm:px-4">
        {sections.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            // `end` só na raiz: sem isso "Início" ficaria ativo em toda rota,
            // já que todo caminho começa com "/".
            end={to === '/'}
            className={({ isActive }) =>
              cn(
                'flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm transition-colors',
                isActive
                  ? 'border-brand-600 font-medium text-brand-700'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  className={cn('size-4 shrink-0', isActive ? 'text-brand-600' : 'text-slate-400')}
                  aria-hidden
                />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
